/**
 * Custom axios retry implementation
 * This replaces the dependency on retry-axios which is causing build issues
 */

// Default retry configuration
const defaultRetryConfig = {
  retry: 3,
  retryDelay: 1000,
  statusCodesToRetry: [[500, 599]],
  backoffType: 'exponential',
  onRetryAttempt: null
};

/**
 * Attaches retry interceptor to axios instance
 * @param {Object} axios - The axios instance
 * @param {Object} config - Custom retry configuration
 */
export const setupAxiosRetry = (axios, customConfig = {}) => {
  const config = { ...defaultRetryConfig, ...customConfig };
  
  // Add request interceptor to track retry attempts
  axios.interceptors.request.use(
    (request) => {
      // Initialize retry count if not present
      request.retryCount = request.retryCount || 0;
      return request;
    }
  );
  
  // Add response interceptor to handle retries
  axios.interceptors.response.use(
    // Success handler - just pass through
    (response) => response,
    // Error handler - implement retry logic
    async (error) => {
      const { config, response } = error;
      
      // If no config or retry is disabled, reject immediately
      if (!config || !config.retry) {
        return Promise.reject(error);
      }
      
      // Set retry count if not already set
      config.retryCount = config.retryCount || 0;
      
      // Check if we've reached max retries
      if (config.retryCount >= config.retry) {
        return Promise.reject(error);
      }
      
      // Check if status code is in retry range
      const shouldRetry = () => {
        // No response means network error, always retry
        if (!response) return true;
        
        // Check status code against retry ranges
        const status = response.status;
        return config.statusCodesToRetry.some(([min, max]) => 
          status >= min && status <= max
        );
      };
      
      // If we shouldn't retry, reject immediately
      if (!shouldRetry()) {
        return Promise.reject(error);
      }
      
      // Increment retry count
      config.retryCount += 1;
      
      // Call onRetryAttempt callback if provided
      if (config.onRetryAttempt) {
        config.onRetryAttempt(error);
      }
      
      // Calculate delay based on backoff type
      let delay = config.retryDelay;
      if (config.backoffType === 'exponential') {
        delay = config.retryDelay * Math.pow(2, config.retryCount - 1);
      }
      
      // Wait for the delay
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return axios(config);
    }
  );
};

/**
 * Configure axios instance with retry capability
 * @param {Object} axios - The axios instance to configure
 */
export const configureAxiosRetry = (axios) => {
  // Set default retry config
  axios.defaults.retry = 3;
  axios.defaults.retryDelay = 1000;
  axios.defaults.statusCodesToRetry = [[500, 599]];
  axios.defaults.backoffType = 'exponential';
  axios.defaults.onRetryAttempt = (err) => {
    console.log(`Retry attempt #${err.config.retryCount}`);
  };
  
  // Setup retry interceptor
  setupAxiosRetry(axios);
};